import { useState, useEffect, useCallback } from 'react';
import { useSiteFilter } from '../../../contexts/SiteFilterContext';
import LocationService from '../../../services/locationService';
import {
  buildLocationHierarchy,
  flattenHierarchy,
  extractLocationsFromResponse,
  filterBySite
} from '../utils/locationTransformers';

/**
 * Hook for managing location data fetching and hierarchy
 * Integrates with SiteFilterContext for site-scoped views
 */
export const useLocations = () => {
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();

  const [flatLocations, setFlatLocations] = useState([]);
  const [hierarchicalLocations, setHierarchicalLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Wait for site filter to be ready if active
      if (hasSiteFilter && !Array.isArray(siteLocationIds)) {
        setLoading(false);
        return;
      }

      const response = await LocationService.getAllLocations({
        useCache: !hasSiteFilter,
        forceRefresh: hasSiteFilter
      });

      let { flatLocations: extracted, total: extractedTotal } = extractLocationsFromResponse(response);

      // Filter by site if active
      if (hasSiteFilter && Array.isArray(siteLocationIds)) {
        extracted = filterBySite(extracted, siteLocationIds);
        extractedTotal = extracted.length;
      }

      const hierarchy = buildLocationHierarchy(extracted);

      setFlatLocations(extracted);
      setHierarchicalLocations(hierarchy);
      setTotal(extractedTotal);
    } catch (err) {
      console.error('[useLocations] Error loading locations:', err);
      setError(err);
      setFlatLocations([]);
      setHierarchicalLocations([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [hasSiteFilter, siteLocationIds]);

  // Load on mount and when dependencies change
  useEffect(() => {
    loadLocations();
  }, [loadLocations, refreshTrigger]);

  // Refetch when site filter changes
  useEffect(() => {
    if (selectedSiteId !== undefined) {
      loadLocations();
    }
  }, [selectedSiteId, loadLocations]);

  const refresh = useCallback(() => {
    LocationService.invalidateCache();
    setRefreshTrigger(prev => prev + 1);
  }, []);

  return {
    flatLocations,
    hierarchicalLocations,
    loading,
    error,
    total,
    refresh,
    loadLocations
  };
};

export default useLocations;
